import { useCallback } from 'react'
import { getOutgoers, MarkerType, NodeProps, useReactFlow } from 'reactflow'

import { uuid } from '@ui/lib/utils'
import { useSaveUpdateStatusStore } from '@ui/stores'

// this hook implements the logic for clicking a workflow node
// on workflow node click: create a new child node of the clicked node
interface Options {
  skipNextNode?: boolean
}

export function useSaveClick(id: NodeProps['id'], options?: Options) {
  const { setEdges, setNodes, getNodes, getEdges, getNode } = useReactFlow()
  const setPipelineModified = useSaveUpdateStatusStore.getState().setPipelineModified

  const defaultOptions: Options = {
    skipNextNode: false,
  }

  const finalOptions = { ...defaultOptions, ...options }

  const onClick = useCallback(async () => {
    // we need the parent node object for positioning the new child node
    const parentNode = getNode(id)

    if (!parentNode) {
      return
    }

    // If we already have children, we have saved the slice and the loaf. No need to add children
    const existingConnections = getOutgoers(parentNode, getNodes(), getEdges())
    if (existingConnections.length > 0) {
      return
    }

    // create a unique id for the placeholder (the placeholder gets added to the new child node)
    const childPlaceholderId = uuid()

    // create a placeholder for the new child node
    // we want to display a placeholder for all workflow nodes that do not have a child already
    // as the newly created node will not have a child, it gets this placeholder
    const childPlaceholderNode = {
      id: childPlaceholderId,
      // we place the placeholder 150 pixels below the child node, spacing can be adjusted in the useLayout hook
      position: { x: parentNode.position.x + 250, y: parentNode.position.y },
      type: 'placeholder',
      data: {
        parentSliceId: parentNode.id,
        undeletable: true,
      },
    }

    // we need to create a connection from child to our placeholder
    const childPlaceholderEdge = {
      id: `${parentNode.id}=>${childPlaceholderId}`,
      source: parentNode.id,
      target: childPlaceholderId,
      type: 'button',
      style: { strokeDasharray: 5 },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 20,
        height: 20,
      },
    }

    // add the new child node, which is the fancy placeholder
    // Also denote that this node has been modified, so we can push it as an update to the server when the customer
    // is ready to trigger a save
    setNodes((nodes) =>
      nodes
        .map((node) => {
          if (node.id === id) {
            node.data = { ...node.data, modified: true, undeletable: true }
          }

          if (node.data.parentSliceId === parentNode.data.parentSliceId) {
            node.data = { ...node.data, undeletable: true }
          }

          return node
        })
        .concat(finalOptions.skipNextNode ? [] : [childPlaceholderNode])
    )

    // add the new edges (node -> child, child -> placeholder)
    setEdges((edges) =>
      edges
        .map((edge) => {
          // here we are changing the type of the connecting edge from placeholder to workflow
          if (edge.target === id) {
            const showAdd = edge.type && edge.type.match(/button/i) ? 'button' : 'smoothstep'

            return {
              ...edge,
              type: showAdd,
              style: {},
            }
          }
          return edge
        })
        .concat(finalOptions.skipNextNode ? [] : [childPlaceholderEdge])
    )
    setPipelineModified(true)
  }, [finalOptions.skipNextNode, getEdges, getNode, getNodes, id, setEdges, setNodes, setPipelineModified])

  return onClick
}

export default useSaveClick
